'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { motion, AnimatePresence, useSpring, useTransform } from 'framer-motion';
import Link from 'next/link';

// --- Shared Types & Data (Duplicated for Isolation) ---
interface CaseStudy {
    id: string;
    clientName: string;
    industry: string;
    logo: string;
    logoAlt: string;
    color: string;
    stats: {
        visibility: { start: number; end: number; label: string; suffix: string };
        traffic: { start: number; end: number; label: string; suffix: '' };
        ranking: { start: number; end: number; label: string; suffix: '' };
    };
    trendData: number[];
    testimonial: string;
    author: string;
    role: string;
    achievements: string[];
}

const caseStudies: Record<string, CaseStudy> = {
    pincode: {
        id: 'pincode',
        clientName: 'Pincode Hotels',
        industry: 'Hospitality',
        logo: "https://images.unsplash.com/photo-1581519925725-5ae2e08a0757",
        logoAlt: 'Luxury hotel exterior',
        color: 'text-amber-500',
        stats: {
            visibility: { start: 42, end: 89, label: 'Brand Visibility', suffix: '%' },
            traffic: { start: 1200, end: 4800, label: 'Monthly Traffic', suffix: '' },
            ranking: { start: 15, end: 3, label: 'Avg Google Rank', suffix: '' },
        },
        trendData: [1200, 1850, 2400, 3100, 3950, 4800],
        testimonial: "Site Map Genius transformed our online presence. Bookings increased by 285% within 6 months.",
        author: 'Rajesh Kumar',
        role: 'General Manager',
        achievements: [
            'Google Business Profile Verified',
            'Top 3 Local Rankings (15+ Keywords)',
            'Auto-Review Management System',
            'SEO-Optimized Content Strategy'
        ]
    },
    ak47: {
        id: 'ak47',
        clientName: 'AK47Sports',
        industry: 'E-commerce',
        logo: "https://images.unsplash.com/photo-1648657850347-81445c3978ae",
        logoAlt: 'Sports equipment',
        color: 'text-brand-primary',
        stats: {
            visibility: { start: 38, end: 91, label: 'Brand Visibility', suffix: '%' },
            traffic: { start: 850, end: 3400, label: 'Monthly Traffic', suffix: '' },
            ranking: { start: 22, end: 2, label: 'Avg Google Rank', suffix: '' },
        },
        trendData: [850, 1100, 1650, 2200, 2900, 3400],
        testimonial: "The Google Business Profile optimization alon brought us 150+ new customers monthly.",
        author: 'Amit Sharma',
        role: 'Founder',
        achievements: [
            '139% Visibility Increase',
            'Dominated "Cricket Gear" Niche',
            '400% Organic Traffic Growth',
            '24/7 Rank Monitoring Active'
        ]
    }
};

// --- Mobile Optimized Components ---

const MetricCard = ({ label, start, end, suffix, color }: { label: string, start: number, end: number, suffix: string, color: string }) => {
    const spring = useSpring(start, { bounce: 0, duration: 2000 });
    const display = useTransform(spring, (current) => Math.round(current).toLocaleString() + suffix);

    useEffect(() => {
        spring.set(end);
    }, [end, spring]);

    return (
        <div className="bg-slate-50/50 rounded-xl p-3 border border-slate-100 flex flex-col items-center justify-center text-center">
            <motion.div className={`text-xl font-bold ${color}`}>
                {display}
            </motion.div>
            <div className="text-[10px] text-slate-500 font-medium mt-0.5 uppercase tracking-wide leading-tight">{label}</div>
        </div>
    );
};

const GrowthChartMobile = ({ data, colorClass }: { data: number[], colorClass: string }) => {
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min;

    const points = data.map((val, i) => {
        const x = (i / (data.length - 1)) * 100;
        const y = 100 - ((val - min) / range) * 80;
        return `${x},${y}`;
    }).join(' ');

    const areaPath = `M 0,100 ${points} 100,100 Z`;
    const linePath = `M ${points}`;
    const strokeColor = colorClass.includes('amber') ? '#f59e0b' : '#2563eb';

    return (
        <div className="w-full h-24 relative overflow-hidden">
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
                <defs>
                    <linearGradient id={`grad-m-${strokeColor}`} x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor={strokeColor} stopOpacity="0.2" />
                        <stop offset="100%" stopColor={strokeColor} stopOpacity="0" />
                    </linearGradient>
                </defs>
                <motion.path
                    d={areaPath}
                    fill={`url(#grad-m-${strokeColor})`}
                    initial={{ opacity: 0, pathLength: 0 }}
                    whileInView={{ opacity: 1, pathLength: 1 }}
                    transition={{ duration: 1.5 }}
                />
                <motion.path
                    d={linePath}
                    fill="none"
                    stroke={strokeColor}
                    strokeWidth="3"
                    initial={{ pathLength: 0 }}
                    whileInView={{ pathLength: 1 }}
                    transition={{ duration: 1.5, ease: "easeOut" }}
                />
            </svg>
        </div>
    );
};

const MobileSuccessMetrics = () => {
    const [activeId, setActiveId] = useState<string>('pincode');
    const activeData = caseStudies[activeId];
    // Duplicated timestamp logic
    const [lastUpdated, setLastUpdated] = useState<string>('Today');
    useEffect(() => {
        const now = new Date();
        setLastUpdated(`Today, ${now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`);
    }, []);

    return (
        <section id="measurable-results" className="py-16 bg-slate-50 relative overflow-hidden scroll-mt-20">
            {/* Simple Background Gradient for Mobile Performance */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-50/20 to-transparent pointer-events-none" />

            <div className="relative z-10 px-4">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center space-x-2 bg-brand-primary/10 px-3 py-1.5 rounded-full mb-3">
                        <Icon name="ChartBarIcon" size={16} className="text-brand-primary" variant="solid" />
                        <span className="text-xs font-bold text-brand-primary uppercase tracking-wide">Growth Engine</span>
                    </div>
                    <h2 className="text-3xl font-bold text-slate-900 leading-tight">
                        Measurable <span className="text-brand-primary">Results</span>
                    </h2>
                </div>

                {/* --- Option A: Story Tabs (Horizontal Scroll) --- */}
                <div className="flex overflow-x-auto gap-4 pb-4 mb-2 no-scrollbar snap-x snap-mandatory">
                    {/* Add padding start to align first item */}
                    <div className="w-1 shrink-0" /> {/* Spacer */}

                    {Object.values(caseStudies).map((study) => (
                        <button
                            key={study.id}
                            onClick={() => setActiveId(study.id)}
                            className={`flex flex-col items-center gap-2 min-w-[80px] snap-start transition-all duration-300 ${activeId === study.id ? 'opacity-100 scale-105' : 'opacity-60 grayscale hover:opacity-80'
                                }`}
                        >
                            <div className={`w-16 h-16 rounded-full p-0.5 ${activeId === study.id
                                    ? `bg-gradient-to-tr from-brand-primary to-${study.color.replace('text-', '')}`
                                    : 'bg-slate-200'
                                }`}>
                                <div className="w-full h-full rounded-full border-2 border-white overflow-hidden bg-white">
                                    <AppImage src={study.logo} alt={study.logoAlt} className="object-cover w-full h-full" />
                                </div>
                            </div>
                            <span className={`text-[10px] font-bold truncate max-w-full ${activeId === study.id ? 'text-slate-900' : 'text-slate-500'
                                }`}>
                                {study.clientName}
                            </span>
                        </button>
                    ))}

                    {/* "More" Placeholders for visual scrolling cue */}
                    <div className="flex flex-col items-center gap-2 min-w-[80px] opacity-40">
                        <div className="w-16 h-16 rounded-full bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center">
                            <Icon name="PlusIcon" size={20} className="text-slate-400" />
                        </div>
                        <span className="text-[10px] font-medium text-slate-400">Loading...</span>
                    </div>
                </div>

                {/* Dashboard Card */}
                <AnimatePresence mode='wait'>
                    <motion.div
                        key={activeId}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden mt-4"
                    >
                        {/* Header */}
                        <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/80 flex justify-between items-center">
                            <div className="flex items-center gap-2">
                                <div className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-[10px] font-bold flex items-center">
                                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1.5 animate-pulse" />
                                    VERIFIED
                                </div>
                            </div>
                            <span className="text-[10px] text-slate-400">{lastUpdated}</span>
                        </div>

                        <div className="p-5">
                            {/* Stats Grid */}
                            <div className="grid grid-cols-3 gap-2 mb-6">
                                <MetricCard
                                    label="Visibility"
                                    start={activeData.stats.visibility.start}
                                    end={activeData.stats.visibility.end}
                                    suffix={activeData.stats.visibility.suffix}
                                    color={activeData.color}
                                />
                                <MetricCard
                                    label="Traffic"
                                    start={activeData.stats.traffic.start}
                                    end={activeData.stats.traffic.end}
                                    suffix={activeData.stats.traffic.suffix}
                                    color={activeData.color}
                                />
                                <MetricCard
                                    label="Rank"
                                    start={activeData.stats.ranking.start}
                                    end={activeData.stats.ranking.end}
                                    suffix={activeData.stats.ranking.suffix}
                                    color={activeData.color}
                                />
                            </div>

                            {/* Chart */}
                            <div className="mb-6">
                                <div className="flex justify-between items-baseline mb-2">
                                    <h4 className="text-sm font-bold text-slate-900">Growth Trend</h4>
                                    <span className={`text-sm font-bold ${activeData.color}`}>
                                        +{Math.round((activeData.stats.traffic.end - activeData.stats.traffic.start) / activeData.stats.traffic.start * 100)}%
                                    </span>
                                </div>
                                <GrowthChartMobile data={activeData.trendData} colorClass={activeData.color} />
                            </div>

                            {/* Testimonial & Author */}
                            <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 mb-4">
                                <p className="text-slate-700 italic text-sm mb-3 leading-relaxed">"{activeData.testimonial}"</p>
                                <div className="flex items-center gap-3">
                                    <div className={`w-8 h-8 rounded-full ${activeData.color.replace('text-', 'bg-')} bg-opacity-10 flex items-center justify-center`}>
                                        <span className={`text-xs font-bold ${activeData.color}`}>{activeData.author[0]}</span>
                                    </div>
                                    <div>
                                        <div className="font-bold text-slate-900 text-xs">{activeData.author}</div>
                                        <div className="text-[10px] text-slate-500">{activeData.role}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Key Wins (Pills) */}
                            <div className="flex flex-wrap gap-2">
                                {activeData.achievements.slice(0, 3).map((item, i) => (
                                    <span key={i} className="text-[10px] font-medium text-slate-600 bg-slate-100 px-2 py-1 rounded-md border border-slate-200">
                                        ✓ {item}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </AnimatePresence>

                {/* CTA */}
                <div className="mt-8 text-center px-4">
                    <p className="text-sm text-slate-500 mb-4">Ready to reach new heights?</p>
                    <Link
                        href="/contact"
                        className="block w-full py-4 bg-slate-900 text-white rounded-xl font-bold shadow-lg shadow-slate-900/10 hover:bg-slate-800 transition-colors"
                    >
                        Start Your Growth
                    </Link>
                </div>

            </div>

            <style jsx>{`
                .no-scrollbar::-webkit-scrollbar {
                    display: none;
                }
                .no-scrollbar {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>
        </section>
    );
};

export default MobileSuccessMetrics;
